import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminTeamDTO } from "@shaarp/common";

function getDiscountState(
  localCode: string | null | undefined,
  stripeCode: string | null | undefined,
  percentOff: number | null | undefined,
  hasSubscription: boolean
): {
  variant: "success" | "warning" | "outline" | "secondary";
  tooltip: string;
  hasCode: boolean;
} {
  const local = localCode || null;
  const stripe = stripeCode || null;

  if (!local && !stripe) {
    return { variant: "outline", tooltip: "", hasCode: false };
  }

  if (hasSubscription && local !== stripe) {
    const localLabel = local ?? "-";
    const stripeLabel = stripe
      ? `${stripe}${percentOff != null ? ` (-${percentOff}%)` : ""}`
      : "-";
    return {
      variant: "warning",
      tooltip: `Local: ${localLabel} / Stripe: ${stripeLabel}`,
      hasCode: true,
    };
  }

  const code = stripe || local;
  const label =
    code && percentOff != null ? `${code} (-${percentOff}%)` : (code ?? "");

  return {
    variant: local || stripe ? "success" : "outline",
    tooltip: label,
    hasCode: !!(local || stripe),
  };
}

export default function DataTableCellAdminsDiscounts({
  team,
}: {
  team: AdminTeamDTO;
}) {
  const { t } = useTranslation();

  const monthly = getDiscountState(
    team.monthlyDiscount,
    team.stripeMonthlyPromotionCode,
    team.stripeMonthlyPercentOff,
    team.hasMonthlySubscription
  );

  const annual = getDiscountState(
    team.annualDiscount,
    team.stripeAnnualPromotionCode,
    team.stripeAnnualPercentOff,
    team.hasAnnualSubscription
  );

  if (!monthly.hasCode && !annual.hasCode) {
    return null;
  }

  return (
    <div className="flex flex-row gap-1">
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge variant={monthly.variant}>M</Badge>
        </TooltipTrigger>
        <TooltipContent>
          {monthly.hasCode
            ? monthly.tooltip
            : t("admin.dataTableAdminTeams.tooltips.none")}
        </TooltipContent>
      </Tooltip>

      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge variant={annual.variant}>A</Badge>
        </TooltipTrigger>
        <TooltipContent>
          {annual.hasCode
            ? annual.tooltip
            : t("admin.dataTableAdminTeams.tooltips.none")}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
